import React, { useState, useMemo } from 'react';
import { useFormat } from '../../../i18n/format.js';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { X, Car, Shield, ChevronDown, ChevronRight, CheckCircle } from 'lucide-react';
import { resolveCoverageLevel } from '../../../utils/insuranceStatus.js';

// Les libelles de couverture sont resolus par t('insurance.coverageShort.<code>').

const COVERAGE_COLORS = {
  tous_risques: 'bg-green-100 text-green-700',
  tiers_plus: 'bg-blue-100 text-blue-700',
  tiers_simple: 'bg-gray-100 text-gray-700',
  standard: 'bg-purple-100 text-purple-700',
};

function InsuredVehiclesModal({ vehicleInsurances, vehicles, contracts, coverageModels = [], insuredVehicleIds, onClose }) {
  const { t, fmt } = useFormat();
  // Primes de filiales différentes : conversion avant addition, affichage ligne à ligne
  // dans la devise de la filiale.
  const { money, moneyScope, sumScope } = useOrgCurrency();
  const [groupBy, setGroupBy] = useState('contract');
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const getVehicle = (id) => vehicles.find(v => v.id === id);
  const getContract = (id) => contracts.find(c => c.id === id);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const relevantInsurances = useMemo(() =>
    vehicleInsurances.filter(vi => insuredVehicleIds.has(vi.vehicle_id)),
    [vehicleInsurances, insuredVehicleIds]
  );

  const totalPremium = useMemo(() =>
    sumScope(relevantInsurances, vi => vi.premium_amount),
    [relevantInsurances, sumScope]
  );

  const avgPremium = totalPremium.complete && insuredVehicleIds.size > 0
    ? totalPremium.value / insuredVehicleIds.size
    : null;

  const groupedByContract = useMemo(() => {
    const groups = {};
    relevantInsurances.forEach(vi => {
      const key = vi.contract_id || 0;
      if (!groups[key]) groups[key] = [];
      groups[key].push(vi);
    });
    return groups;
  }, [relevantInsurances]);

  const groupedByCoverage = useMemo(() => {
    const groups = {};
    relevantInsurances.forEach(vi => {
      const key = resolveCoverageLevel(vi, coverageModels) || 'standard';
      if (!groups[key]) groups[key] = [];
      groups[key].push(vi);
    });
    return groups;
  }, [relevantInsurances, coverageModels]);

  const VehicleRow = ({ vi }) => {
    const vehicle = getVehicle(vi.vehicle_id);
    const coverageKey = resolveCoverageLevel(vi, coverageModels) || 'standard';
    return (
      <tr className="hover:bg-gray-50">
        <td className="py-2.5 pr-4">
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 text-gray-400" />
            <span className="font-medium text-gray-900">{vehicle?.license_plate || t('common.states.notAvailable')}</span>
          </div>
        </td>
        <td className="py-2.5 pr-4 text-sm text-gray-600">
          {vehicle ? `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || '—' : '—'}
        </td>
        <td className="py-2.5 pr-4">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${COVERAGE_COLORS[coverageKey] || COVERAGE_COLORS.standard}`}>
            {t(`insurance.coverageShort.${coverageKey}`, { defaultValue: coverageKey })}
          </span>
        </td>
        <td className="py-2.5 pr-4 text-sm text-right font-medium text-gray-900">
          {vi.premium_amount ? money(vi.premium_amount, vi) : '—'}
        </td>
        <td className="py-2.5 text-sm text-gray-500">
          {vi.assigned_at ? fmt.date(vi.assigned_at) : '—'}
        </td>
      </tr>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[900px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-white" />
            <h2 className="text-lg font-semibold text-white">
              {t('insurance.insuredModal.title', { count: insuredVehicleIds.size })}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Summary Bar */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
              <p className="text-2xl font-bold text-gray-900">{insuredVehicleIds.size}</p>
              <p className="text-xs text-gray-500">{t('insurance.insuredModal.coveredVehicles')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
              <p className="text-2xl font-bold text-gray-900">{totalPremium.label}</p>
              <p className="text-xs text-gray-500">{t('insurance.insuredModal.totalPremium')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
              <p className="text-2xl font-bold text-gray-900">{avgPremium != null ? moneyScope(avgPremium) : '—'}</p>
              <p className="text-xs text-gray-500">{t('insurance.insuredModal.avgPremium')}</p>
            </div>
          </div>

          {/* Group Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
            <button
              onClick={() => setGroupBy('contract')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                groupBy === 'contract' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('insurance.insuredModal.byContract')}
            </button>
            <button
              onClick={() => setGroupBy('coverage')}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                groupBy === 'coverage' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('insurance.insuredModal.byCoverage')}
            </button>
          </div>

          {/* Grouped Content */}
          {groupBy === 'contract' && (
            <div className="space-y-3">
              {Object.entries(groupedByContract).map(([contractId, vis]) => {
                const contract = getContract(parseInt(contractId));
                const key = `contract-${contractId}`;
                const isOpen = expandedGroups.has(key);
                const subtotal = sumScope(vis, vi => vi.premium_amount);

                return (
                  <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleGroup(key)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                        <Shield className="w-4 h-4 text-blue-500" />
                        <span className="font-medium text-gray-900">
                          {contract?.policy_number || t('insurance.insuredModal.noContract')} — {contract?.insurer_name || ''}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                          {t('insurance.insuredModal.vehicleCount', { count: vis.length })}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{subtotal.label}</span>
                    </button>

                    {isOpen && (
                      <div className="p-4">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                              <th className="pb-2 pr-4">{t('insurance.insuredModal.col.plate')}</th>
                              <th className="pb-2 pr-4">{t('insurance.insuredModal.col.makeModel')}</th>
                              <th className="pb-2 pr-4">{t('insurance.insuredModal.col.coverage')}</th>
                              <th className="pb-2 pr-4 text-right">{t('insurance.insuredModal.col.premium')}</th>
                              <th className="pb-2">{t('insurance.insuredModal.col.assignedAt')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {vis.map(vi => <VehicleRow key={vi.id} vi={vi} />)}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {groupBy === 'coverage' && (
            <div className="space-y-3">
              {Object.entries(groupedByCoverage).map(([coverageKey, vis]) => {
                const key = `coverage-${coverageKey}`;
                const isOpen = expandedGroups.has(key);
                const subtotal = sumScope(vis, vi => vi.premium_amount);
                const label = t(`insurance.coverageShort.${coverageKey}`, { defaultValue: coverageKey });
                const colorClass = COVERAGE_COLORS[coverageKey] || COVERAGE_COLORS.standard;

                return (
                  <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleGroup(key)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClass}`}>{label}</span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">
                          {t('insurance.insuredModal.vehicleCount', { count: vis.length })}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{subtotal.label}</span>
                    </button>

                    {isOpen && (
                      <div className="p-4">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                              <th className="pb-2 pr-4">{t('insurance.insuredModal.col.plate')}</th>
                              <th className="pb-2 pr-4">{t('insurance.insuredModal.col.makeModel')}</th>
                              <th className="pb-2 pr-4">{t('insurance.insuredModal.col.coverage')}</th>
                              <th className="pb-2 pr-4 text-right">{t('insurance.insuredModal.col.premium')}</th>
                              <th className="pb-2">{t('insurance.insuredModal.col.assignedAt')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {vis.map(vi => <VehicleRow key={vi.id} vi={vi} />)}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {relevantInsurances.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {t('insurance.insuredModal.empty')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InsuredVehiclesModal;
